import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Wrench, ShoppingCart, Minus, Plus, ChevronRight, ShieldCheck, Package, FileText,
  Star, MessageSquarePlus, ListChecks, MessageCircle, Truck, RotateCcw, Store, BadgeCheck,
  Facebook, Twitter, Phone, Info, ChevronLeft, X,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { formatFCFA, effectivePrice, hasPromo, discountPercent, isLowStock, formatDate } from "@/lib/format";
import { Loader, StarRating } from "@/components/common";
import ProductCard from "@/components/ProductCard";
import PromoCountdown from "@/components/PromoCountdown";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import InstallRequestModal from "@/components/InstallRequestModal";
import QuoteRequestModal from "@/components/QuoteRequestModal";
import Modal, { TextArea } from "@/components/Modal";

const SECTIONS = [
  { key: "details", label: "Détails", Icon: FileText },
  { key: "fiche", label: "Fiche technique", Icon: ListChecks },
  { key: "avis", label: "Commentaires clients vérifiés", Icon: MessageCircle },
];

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [zoom, setZoom] = useState({ show: false, x: 50, y: 50 });
  const [lbOpen, setLbOpen] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [rev, setRev] = useState({ reviews: [], average: 0, count: 0 });
  const [related, setRelated] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeSection, setActiveSection] = useState("details");

  const loadReviews = useCallback(() => api.get(`/products/${id}/reviews`).then((r) => setRev(r.data)).catch(() => {}), [id]);
  useEffect(() => {
    setP(null); setNotFound(false); setActiveImg(0); setQty(1);
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      if (r.data?.category_id) {
        api.get(`/products?category=${r.data.category_id}`)
          .then((rr) => setRelated((rr.data || []).filter((x) => x.id !== id).slice(0, 10)))
          .catch(() => {});
      }
    }).catch(() => setNotFound(true));
    loadReviews();
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    try {
      const prev = JSON.parse(localStorage.getItem("togobati_recent") || "[]").filter((x) => x !== id);
      localStorage.setItem("togobati_recent", JSON.stringify([id, ...prev].slice(0, 12)));
    } catch { /* ignore */ }
  }, [id, loadReviews]);

  const imgCount = (p?.images || []).length;
  useEffect(() => {
    if (!lbOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLbOpen(false);
      else if (e.key === "ArrowRight") setActiveImg((i) => (i + 1) % Math.max(imgCount, 1));
      else if (e.key === "ArrowLeft") setActiveImg((i) => (i - 1 + Math.max(imgCount, 1)) % Math.max(imgCount, 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lbOpen, imgCount]);

  if (notFound) return (
    <div className="py-20 text-center" data-testid="product-not-found">
      <Package className="w-10 h-10 mx-auto text-slate-300 mb-3" />
      <p className="text-slate-500">Produit introuvable.</p>
      <Link to="/recherche" className="inline-block mt-4 text-[#C85A32] font-semibold hover:underline">Retour au catalogue</Link>
    </div>
  );
  if (!p) return <Loader />;

  const images = p.images && p.images.length ? p.images : [];
  const outOfStock = p.stock <= 0;
  const cat = categories.find((c) => c.id === p.category_id);
  const buy = () => { add(p, qty); toast.success("Ajouté au panier"); };
  const scrollTo = (key) => { setActiveSection(key); document.getElementById(`sec-${key}`)?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  const specLines = (p.specs || "").split("\n").map((s) => s.trim()).filter(Boolean);
  const stockLabel = outOfStock ? "En rupture de stock" : isLowStock(p) ? `Bientôt épuisé (${p.stock} ${p.unit})` : `En stock (${p.stock} ${p.unit})`;
  const descRows = [
    ["Référence (SKU)", p.reference],
    ["Marque", p.brand],
    ["Catégorie", cat?.name],
    ["Sous-catégorie", p.subcategory],
    ["Unité de vente", p.unit],
    ["Disponibilité", stockLabel],
    ["Installation", p.is_pack ? "Pose incluse (pack)" : p.installation_type === "fixed" ? `Disponible — ${formatFCFA(p.installation_price)}` : p.installation_type === "quote" ? "Sur devis" : "Non disponible"],
    ["Livraison", "Lomé, Agoè & Baguida sous 24–72h"],
    ["Garantie", "Produit conforme — retours sous 7 jours (non posé)"],
  ].filter((r) => r[1]);

  const MiniCard = () => (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3" data-testid="mini-product-card">
      <div className="w-14 h-14 rounded-lg bg-slate-100 overflow-hidden shrink-0">
        {images[0] ? <img src={images[0]} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><Package className="w-6 h-6" /></div>}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
        <p className="font-num font-black text-[#C85A32]">{formatFCFA(effectivePrice(p))}</p>
        {hasPromo(p) && <span className="text-[11px] text-slate-400 line-through font-num mr-1">{formatFCFA(p.price)}</span>}
        {hasPromo(p) && <span className="text-[11px] font-bold text-[#E08B00]">-{discountPercent(p)}%</span>}
      </div>
    </div>
  );

  return (
    <div className="pb-28 md:pb-0">
      {lbOpen && images[activeImg] && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center" onClick={() => setLbOpen(false)} data-testid="lightbox">
          <button className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" onClick={(e) => { e.stopPropagation(); setLbOpen(false); }} data-testid="lightbox-close"><X className="w-6 h-6" /></button>
          {images.length > 1 && (
            <>
              <button className="absolute left-3 sm:left-6 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i - 1 + images.length) % images.length); }} data-testid="lightbox-prev"><ChevronLeft className="w-6 h-6" /></button>
              <button className="absolute right-3 sm:right-6 w-11 h-11 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" onClick={(e) => { e.stopPropagation(); setActiveImg((i) => (i + 1) % images.length); }} data-testid="lightbox-next"><ChevronRight className="w-6 h-6" /></button>
            </>
          )}
          <img src={images[activeImg]} alt={p.name} className="max-w-[92vw] max-h-[86vh] object-contain" onClick={(e) => e.stopPropagation()} data-testid="lightbox-image" />
          {images.length > 1 && <span className="absolute bottom-5 text-white/80 text-sm font-num">{activeImg + 1} / {images.length}</span>}
        </div>
      )}
      {/* Fil d'Ariane */}
      <nav className="flex items-center flex-wrap gap-1 text-xs text-slate-400 mb-4" data-testid="breadcrumb">
        <Link to="/" className="hover:text-[#C85A32]">Accueil</Link>
        <ChevronRight className="w-3 h-3" />
        <Link to="/recherche" className="hover:text-[#C85A32]">Catalogue</Link>
        {cat && (<><ChevronRight className="w-3 h-3" /><Link to={`/recherche?category=${cat.id}`} className="hover:text-[#C85A32]">{cat.name}</Link></>)}
        <ChevronRight className="w-3 h-3" />
        <span className="text-slate-600 font-medium truncate max-w-[220px]">{p.name}</span>
      </nav>

      {/* ===== Bloc principal : galerie + infos + sidebar ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Galerie */}
          <div>
            <div
              className="aspect-square bg-white border border-slate-100 rounded-2xl overflow-hidden relative group"
              onMouseMove={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setZoom({ show: true, x: ((e.clientX - r.left) / r.width) * 100, y: ((e.clientY - r.top) / r.height) * 100 });
              }}
              onMouseLeave={() => setZoom((z) => ({ ...z, show: false }))}
              onClick={() => images[activeImg] && setLbOpen(true)}
              role="button"
              data-testid="gallery-main"
            >
              {hasPromo(p) && <span className="absolute top-3 left-3 z-10 bg-[#DC2626] text-white text-xs font-bold px-2.5 py-1 rounded-full">-{discountPercent(p)}%</span>}
              {images[activeImg] ? (
                <img
                  src={images[activeImg]} alt={p.name}
                  className="w-full h-full object-contain transition-transform duration-150 cursor-zoom-in"
                  style={zoom.show ? { transform: "scale(2)", transformOrigin: `${zoom.x}% ${zoom.y}%` } : { transform: "scale(1)" }}
                />
              ) : <div className="w-full h-full flex items-center justify-center text-slate-300"><Package className="w-16 h-16" /></div>}
              {images[activeImg] && <span className="absolute bottom-2 right-2 bg-black/40 text-white text-[10px] px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition pointer-events-none">Cliquez pour agrandir · survolez pour zoomer</span>}
            </div>
            {images.length > 1 && (
              <div className="flex gap-2 mt-3 overflow-x-auto pb-1" data-testid="thumbnails">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} className={`w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 transition ${i === activeImg ? "border-[#C85A32]" : "border-slate-200 opacity-70 hover:opacity-100"}`} data-testid={`thumb-${i}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
            <div className="mt-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Partagez ce produit</p>
              <div className="flex items-center gap-2" data-testid="share-row">
                {[Facebook, Twitter, MessageCircle].map((I, i) => (
                  <span key={i} className="w-9 h-9 rounded-full border border-slate-200 text-slate-500 flex items-center justify-center hover:border-[#C85A32] hover:text-[#C85A32] transition cursor-pointer"><I className="w-4 h-4" /></span>
                ))}
              </div>
            </div>
          </div>

          {/* Infos */}
          <div>
            <span className="inline-flex items-center gap-1 bg-[#0F172A] text-white text-[11px] font-bold px-2.5 py-1 rounded" data-testid="official-badge"><BadgeCheck className="w-3.5 h-3.5" /> Boutique Officielle</span>
            <h1 className="font-display text-2xl lg:text-3xl font-extrabold text-[#0F172A] leading-tight mt-2">{p.name}</h1>
            {p.brand && <p className="text-sm text-slate-500 mt-1">Marque : <span className="text-[#C85A32] font-semibold">{p.brand}</span></p>}

            <div className="mt-3 flex items-baseline gap-2 flex-wrap">
              <span className="font-num font-black text-[#0F172A] text-3xl">{formatFCFA(effectivePrice(p))}</span>
              {hasPromo(p) && <span className="font-num text-base text-slate-400 line-through">{formatFCFA(p.price)}</span>}
              {hasPromo(p) && <span className="font-num text-sm font-bold text-[#E08B00]">-{discountPercent(p)}%</span>}
            </div>
            {hasPromo(p) && p.promo_end && <div className="mt-2"><PromoCountdown end={p.promo_end} /></div>}

            <div className="mt-2 text-sm">
              {outOfStock ? <span className="text-red-600 font-semibold">Rupture de stock</span>
                : isLowStock(p) ? <span className="text-amber-600 font-semibold">Plus que {p.stock} {p.unit} — dépêchez-vous !</span>
                : <span className="text-green-600 font-semibold">Disponible ({p.stock} {p.unit})</span>}
            </div>

            {rev.count > 0 && (
              <div onClick={() => scrollTo("avis")} className="mt-2 flex items-center gap-1.5 cursor-pointer w-fit" data-testid="rating-summary">
                <StarRating value={Math.round(rev.average)} readOnly size={15} />
                <span className="text-sm text-[#C85A32] hover:underline">({rev.count} avis vérifiés)</span>
              </div>
            )}

            {/* Quantité + J'achète */}
            <div className="mt-5 flex items-center gap-3">
              <div className="flex items-center border border-slate-300 rounded-lg">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-2.5" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
                <span className="w-10 text-center font-semibold font-num" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty((q) => q + 1)} className="p-2.5" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
              </div>
              <button disabled={outOfStock} onClick={buy}
                className="flex-1 bg-[#E08B00] hover:brightness-105 disabled:opacity-50 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition active:scale-95" data-testid="add-to-cart-button">
                <ShoppingCart className="w-5 h-5" /> J'achète
              </button>
            </div>

            {outOfStock && <RestockAlert productId={p.id} defaultEmail={user?.email} />}

            {/* Promotions / aide */}
            <div className="mt-4 space-y-2 border-t border-slate-100 pt-4" data-testid="promo-info">
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Promotions</p>
              <p className="flex items-start gap-2 text-sm text-slate-600"><Phone className="w-4 h-4 text-[#C85A32] mt-0.5 shrink-0" /> Besoin d'aide pour commander ? Appelez-nous au <span className="font-semibold">+228 71 33 33 46</span></p>
              <p className="flex items-start gap-2 text-sm text-slate-600"><Info className="w-4 h-4 text-[#C85A32] mt-0.5 shrink-0" /> Livraison et pose disponibles à Lomé, Agoè et Baguida.</p>
            </div>

            {p.installation_type === "fixed" && (
              <div className="mt-4 bg-[#FDF3EE] border border-[#C85A32]/20 rounded-xl p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]"><Wrench className="w-4 h-4 text-[#C85A32]" /> Installation disponible — {formatFCFA(p.installation_price)}</div>
                <button onClick={() => setShowInstall(true)} className="mt-3 w-full bg-[#0F172A] text-white font-bold py-2.5 rounded-lg hover:bg-slate-800 transition active:scale-95" data-testid="request-install-btn">Demander l'installation</button>
              </div>
            )}
            {p.installation_type === "quote" && (
              <div className="mt-4 bg-[#FDF3EE] border border-[#C85A32]/20 rounded-xl p-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-[#0F172A]"><FileText className="w-4 h-4 text-[#C85A32]" /> Installation sur devis</div>
                <button onClick={() => setShowQuote(true)} className="mt-3 w-full bg-[#0F172A] text-white font-bold py-2.5 rounded-lg hover:bg-slate-800 transition active:scale-95" data-testid="request-quote-btn">Demander un devis</button>
              </div>
            )}
          </div>
        </div>

          {/* Détails */}
          <section id="sec-details" className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 scroll-mt-24" data-testid="section-details">
            <h2 className="font-display text-xl font-extrabold text-[#0F172A] pb-3 border-b border-slate-100">Détails</h2>
            <p className="mt-4 text-sm text-slate-700 leading-relaxed whitespace-pre-line">
              {p.description || "Aucune description détaillée n'est disponible pour ce produit pour le moment."}
            </p>
            {p.is_pack && (p.pack_contents || []).length > 0 && (
              <div className="mt-4 bg-[#FDF3EE] border border-[#C85A32]/20 rounded-xl p-4" data-testid="pack-contents">
                <h3 className="font-semibold text-sm mb-2 flex items-center gap-1.5"><Package className="w-4 h-4 text-[#C85A32]" /> Ce pack comprend — pose incluse</h3>
                <ul className="space-y-1">
                  {p.pack_contents.map((it, i) => <li key={i} className="text-sm text-slate-600 flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#C85A32]" /> {it}</li>)}
                </ul>
              </div>
            )}
          </section>

          {/* Fiche technique */}
          <section id="sec-fiche" className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 scroll-mt-24" data-testid="section-fiche">
            <h2 className="font-display text-xl font-extrabold text-[#0F172A] pb-3 border-b border-slate-100">Fiche technique</h2>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="border border-slate-100 rounded-xl p-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Principales caractéristiques</h3>
                {specLines.length ? (
                  <ul className="space-y-1.5">
                    {specLines.map((s, i) => <li key={i} className="text-sm text-slate-700 flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#C85A32] mt-1.5 shrink-0" /> {s}</li>)}
                  </ul>
                ) : <p className="text-sm text-slate-400">Caractéristiques non renseignées.</p>}
              </div>
              <div className="border border-slate-100 rounded-xl p-4">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Descriptif technique</h3>
                <dl className="space-y-2">
                  {descRows.map(([k, v], i) => (
                    <div key={i} className="flex justify-between gap-3 text-sm">
                      <dt className="text-slate-500">{k}</dt>
                      <dd className="text-slate-800 font-semibold text-right">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </section>

          {/* Commentaires */}
          <section id="sec-avis" className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 scroll-mt-24" data-testid="product-reviews-section">
            <div className="flex items-center justify-between flex-wrap gap-2 pb-3 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <h2 className="font-display text-xl font-extrabold text-[#0F172A]">Commentaires clients vérifiés</h2>
                {rev.count > 0 && (
                  <span className="text-sm text-slate-500"><span className="font-bold text-slate-800 font-num">{rev.average}</span>/5 · {rev.count} avis</span>
                )}
              </div>
              {user ? (
                <button onClick={() => setShowReview(true)} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#C85A32] border border-[#C85A32]/40 rounded-lg px-3 py-2 hover:bg-[#FDF3EE] transition" data-testid="leave-review-btn">
                  <MessageSquarePlus className="w-4 h-4" /> Laisser un avis
                </button>
              ) : <span className="text-xs text-slate-400">Connectez-vous pour laisser un avis</span>}
            </div>
            {rev.count === 0 ? (
              <div className="mt-4 text-center py-8">
                <Star className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="text-sm text-slate-400">Aucun avis publié. Soyez le premier à donner votre avis !</p>
              </div>
            ) : (
              <div className="mt-4 grid sm:grid-cols-2 gap-3">
                {rev.reviews.map((r, i) => (
                  <div key={i} className="border border-slate-200 rounded-xl p-4" data-testid={`product-review-${i}`}>
                    <div className="flex items-center justify-between">
                      <StarRating value={r.rating} readOnly size={15} />
                      <span className="text-xs text-slate-400">{formatDate(r.created_at)}</span>
                    </div>
                    <p className="font-semibold text-sm text-slate-800 mt-2 flex items-center gap-1.5">
                      {r.customer_name}
                      {r.verified && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700" data-testid={`verified-${i}`}><ShieldCheck className="w-3 h-3" /> Achat vérifié</span>}
                    </p>
                    {r.comment && <p className="text-sm text-slate-600 mt-1 leading-relaxed">{r.comment}</p>}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Sidebar : livraison, vendeur, navigation (sticky) */}
        <aside className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="delivery-card">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-3">Livraison & retours</h3>
            <p className="flex items-start gap-2 text-sm text-slate-600"><Truck className="w-4 h-4 text-[#C85A32] mt-0.5 shrink-0" /> Livraison sous 24-72h à Lomé & environs à partir de <span className="font-semibold">1 000 FCFA</span>.</p>
            <p className="flex items-start gap-2 text-sm text-slate-600 mt-2"><RotateCcw className="w-4 h-4 text-[#C85A32] mt-0.5 shrink-0" /> Retours acceptés sous 7 jours sur les produits non posés.</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="seller-card">
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#0F172A] mb-3">Informations sur le vendeur</h3>
            <div className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-full bg-[#FDF3EE] flex items-center justify-center"><Store className="w-4 h-4 text-[#C85A32]" /></span>
              <div>
                <p className="text-sm font-bold text-slate-800">TOGOBATI</p>
                <p className="text-[11px] text-slate-400">Vendeur officiel — matériaux & pose</p>
              </div>
            </div>
            <div className="mt-3 space-y-1.5 text-sm">
              <p className="flex items-center gap-2 text-slate-600"><ShieldCheck className="w-4 h-4 text-green-600" /> Vitesse d'expédition : <span className="font-semibold">Excellent</span></p>
              <p className="flex items-center gap-2 text-slate-600"><ShieldCheck className="w-4 h-4 text-green-600" /> Qualité produits : <span className="font-semibold">Excellent</span></p>
            </div>
          </div>
          <div className="hidden lg:block lg:sticky lg:top-24 space-y-4">
            <nav className="bg-white border border-slate-200 rounded-2xl p-2" data-testid="section-nav">
              {SECTIONS.map((s) => (
                <button key={s.key} onClick={() => scrollTo(s.key)} data-testid={`nav-${s.key}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition ${activeSection === s.key ? "bg-slate-100 text-[#0F172A]" : "text-slate-600 hover:bg-slate-50"}`}>
                  <s.Icon className="w-4 h-4 text-slate-400" /> {s.label}
                </button>
              ))}
            </nav>
            <div className="bg-white border border-slate-200 rounded-2xl p-3 space-y-3">
              <MiniCard />
              <button disabled={outOfStock} onClick={buy} className="w-full bg-[#E08B00] hover:brightness-105 disabled:opacity-50 text-white font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 transition active:scale-95" data-testid="sidebar-buy-btn">
                <ShoppingCart className="w-4 h-4" /> J'achète
              </button>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-4 text-center" data-testid="questions-card">
              <p className="text-sm text-slate-500">Avez-vous des questions ?</p>
              <Link to="/support" className="mt-1 inline-flex items-center gap-1.5 font-bold text-[#C85A32] hover:underline"><MessageCircle className="w-4 h-4" /> Chat</Link>
            </div>
          </div>
        </aside>
      </div>

      {/* Produits regardés */}
      {related.length > 0 && (
        <section className="mt-8" data-testid="related-products">
          <h2 className="font-display text-xl sm:text-2xl font-extrabold text-[#0F172A] mb-4">Les clients ont également regardé</h2>
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
            {related.map((rp) => (
              <div key={rp.id} className="w-[46%] sm:w-[220px] shrink-0 snap-start" data-testid={`related-card-${rp.id}`}>
                <ProductCard p={rp} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA mobile fixe */}
      <div className="md:hidden fixed bottom-14 inset-x-0 z-30 bg-white border-t border-slate-200 px-4 py-2.5 flex items-center gap-3" data-testid="sticky-cta">
        <div className="leading-tight">
          <p className="font-num font-black text-[#C85A32]">{formatFCFA(effectivePrice(p))}</p>
          <p className="text-[10px] text-slate-400">/ {p.unit}</p>
        </div>
        <button disabled={outOfStock} onClick={buy}
          className="flex-1 bg-[#E08B00] disabled:opacity-50 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 active:scale-95 transition" data-testid="sticky-add-to-cart">
          <ShoppingCart className="w-5 h-5" /> J'achète
        </button>
      </div>

      {showInstall && <InstallRequestModal product={p} onClose={() => setShowInstall(false)} />}
      {showQuote && <QuoteRequestModal product={p} onClose={() => setShowQuote(false)} />}
      {showReview && <ReviewModal product={p} onClose={() => setShowReview(false)} onDone={loadReviews} />}
    </div>
  );
}

function RestockAlert({ productId, defaultEmail }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!email.includes("@")) return toast.error("Entrez un email valide");
    setBusy(true);
    try {
      await api.post(`/products/${productId}/notify-restock`, { email });
      setDone(true);
      toast.success("Vous serez prévenu par email dès le retour en stock");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (done) return (
    <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-700 font-semibold" data-testid="restock-done">
      ✅ Alerte activée ! Nous vous préviendrons à <strong>{email}</strong> dès le retour en stock.
    </div>
  );
  return (
    <div className="mt-4 bg-slate-50 border border-slate-200 rounded-xl p-4" data-testid="restock-alert">
      <p className="text-sm font-bold text-[#0F172A] flex items-center gap-2"><Info className="w-4 h-4 text-[#C85A32]" /> Produit en rupture</p>
      <p className="text-xs text-slate-500 mt-1 mb-3">Recevez un email dès qu'il est de nouveau disponible.</p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.com"
          className="flex-1 border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E08B00]/40" data-testid="restock-email" />
        <button onClick={submit} disabled={busy} className="bg-[#0F172A] text-white font-bold px-4 py-2.5 rounded-lg text-sm disabled:opacity-50 whitespace-nowrap" data-testid="restock-submit">{busy ? "..." : "Prévenez-moi"}</button>
      </div>
    </div>
  );
}

function ReviewModal({ product, onClose, onDone }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!rating) return toast.error("Choisissez une note");
    try {
      await api.post("/reviews", { target_type: "product", target_id: product.id, target_name: product.name, rating, comment });
      toast.success("Merci ! Votre avis sera publié après validation.");
      onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={`Votre avis — ${product.name}`} onClose={onClose} testid="review-modal">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-600 mb-1.5">Votre note</label>
          <StarRating value={rating} onChange={setRating} size={28} />
        </div>
        <TextArea label="Votre commentaire (optionnel)" value={comment} onChange={setComment} testid="review-comment" rows={4} placeholder="Partagez votre expérience avec ce produit…" />
        <p className="text-xs text-slate-400">Votre avis sera vérifié par notre équipe avant publication.</p>
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="review-submit">Envoyer mon avis</button>
      </form>
    </Modal>
  );
}
